// Ekran 03 — Laboratuvar.
// Sınıf adları ref_files/btys-erp-03-laboratuvar.html'den birebir alındı.

import { useEffect, useState, type CSSProperties } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { useSekmeler } from '../../components/Sekmeler'
import { toast } from '../../components/Toast'
import { useFitInsp } from '../../lib/useFitInsp'
import { tr, trTarihSaat } from '../../lib/trFormat'
import { ApiHatasi } from '../../lib/api'
import { useYetki, YetkiKapisi } from '../../lib/yetki'
import { useAnalizFormu } from './AnalizFormu'
import NumuneSekmesi from './NumuneSekmesi'
import YeniNumuneDrawer from './YeniNumuneDrawer'
import {
  analizler as analizApi,
  numuneler as numuneApi,
  type AnalizAlanlari,
  type AnalizDto,
  type NumuneDetay,
  type NumuneDurumu,
  type NumuneOzet,
  type NumuneTipi,
} from './api'
import Sayfalayici from '../../components/Sayfalayici'

const TIPLER: { anahtar: NumuneTipi | 'tumu'; etiket: string }[] = [
  { anahtar: 'tumu', etiket: 'Tümü' },
  { anahtar: 'lot', etiket: 'Lot' },
  { anahtar: 'fermenter', etiket: 'Fermenter' },
  { anahtar: 'urun', etiket: 'Ürün' },
]

export const TIP_ETIKET: Record<NumuneTipi, string> = {
  lot: 'Lot',
  fermenter: 'Fermenter',
  urun: 'Ürün',
}

export const DURUM_ROZET: Record<NumuneDurumu, { metin: string; sinif: string }> = {
  bekliyor: { metin: 'Bekliyor', sinif: 'badge b-amber' },
  analizde: { metin: 'Analizde', sinif: 'badge b-amber' },
  tamamlandi: { metin: 'Analizli', sinif: 'badge b-navy' },
  uygunsuz: { metin: 'Uygunsuz', sinif: 'badge b-red' },
}

/**
 * Tablo "Kaynak" sütunu (ref:481): üst satır kaynağın adı, alt satır lotta
 * cari — fermenter/üründe ise numunenin NEDEN alındığını söyleyen sabit metin.
 * Alt satır her tipte dolu, aksi halde satır yükseklikleri şaşıyor.
 */
export function kaynakMetni(n: NumuneOzet): { ust: string; alt: string } {
  if (n.tip === 'lot') return { ust: n.stokAd ?? '—', alt: n.cariUnvan ?? '—' }
  if (n.tip === 'fermenter')
    return { ust: n.uniteAd ?? n.uniteKod ?? '—', alt: 'proses takibi (K21)' }
  return { ust: n.stokAd ?? 'Ürün', alt: 'ürün kalite (K22)' }
}

/** "15.08 09:30" — referansın tablo biçimi. */
export function kisaZaman(iso: string): string {
  const tam = trTarihSaat(iso)
  if (tam === '—') return tam
  const [tarih, saat] = tam.split(' ')
  const [g, a] = tarih.split('.')
  return `${g}.${a} ${saat}`
}

/** "15.08" — `.dt` sütunu 40px, saat sığmıyor (design-system.css:206). */
export function kisaTarih(iso: string): string {
  const kisa = kisaZaman(iso)
  return kisa === '—' ? kisa : kisa.split(' ')[0]
}

/** Ekran 01/02/04 ile aynı sayfalama adımı. */
const SAYFA_BOYU = 25

function Icerik() {
  const [tip, setTip] = useState<NumuneTipi | 'tumu'>('tumu')
  const [sadeceBekleyen, setSadeceBekleyen] = useState(false)
  const [arama, setArama] = useState('')
  const [sayfa, setSayfa] = useState(1)
  const [seciliId, setSeciliId] = useState<string | null>(null)
  const panel = useFitInsp<HTMLDivElement>()
  const { ac } = useDrawer()
  const sekmeler = useSekmeler()
  const kuyruk = useQueryClient()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('Laboratuvar', 'yaz')

  const { data: ozet } = useQuery({ queryKey: ['lab-ozeti'], queryFn: numuneApi.ozet })

  const { data: hepsi = [], isLoading } = useQuery({
    queryKey: ['numuneler'],
    queryFn: () => numuneApi.liste(),
  })

  // Tip ve "sadece bekleyenler" filtreleri istemcide; sayaçlar filtreden
  // BAĞIMSIZ olmalı (referans drawSeg) — bu yüzden tek liste çekilip
  // burada süzülüyor.
  const liste = hepsi.filter((n) => {
    if (tip !== 'tumu' && n.tip !== tip) return false
    if (sadeceBekleyen && n.durum !== 'bekliyor') return false
    if (arama) {
      const a = arama.toLocaleLowerCase('tr')
      const k = kaynakMetni(n)
      // Havuzda GERÇEK alanlar taranır: `k.alt` fermenter/üründe sabit metin
      // olduğu için yerine kart/ünite kodu konuyor.
      const havuz = [n.no, n.lotBelgeNo, k.ust, n.stokKod, n.uniteKod, n.cariUnvan]
        .filter(Boolean).join(' ')
      if (!havuz.toLocaleLowerCase('tr').includes(a)) return false
    }
    return true
  })

  const sayac = (a: NumuneTipi | 'tumu') =>
    a === 'tumu' ? hepsi.length : hepsi.filter((n) => n.tip === a).length

  const sayfaSayisi = Math.max(1, Math.ceil(liste.length / SAYFA_BOYU))
  const gecerliSayfa = Math.min(sayfa, sayfaSayisi)
  const sayfaListesi = liste.slice((gecerliSayfa - 1) * SAYFA_BOYU, gecerliSayfa * SAYFA_BOYU)

  // Seçim GÖRÜNEN sayfadan kurulur; yoksa panel, listede olmayan bir satırı
  // gösterir.
  const ilkId = sayfaListesi[0]?.id
  useEffect(() => {
    if (ilkId && !sayfaListesi.some((n) => n.id === seciliId)) setSeciliId(ilkId)
  }, [ilkId, sayfaListesi, seciliId])

  const { data: detay } = useQuery({
    queryKey: ['numune', seciliId],
    queryFn: () => numuneApi.detay(seciliId!),
    enabled: !!seciliId,
  })

  function tazele() {
    kuyruk.invalidateQueries({ queryKey: ['numuneler'] })
    kuyruk.invalidateQueries({ queryKey: ['numune'] })
    kuyruk.invalidateQueries({ queryKey: ['lab-ozeti'] })
    kuyruk.invalidateQueries({ queryKey: ['havuzlar'] })
    kuyruk.invalidateQueries({ queryKey: ['lotlar'] })
  }

  const analizEkle = useMutation({
    mutationFn: ({ numuneId, alanlar }: { numuneId: string; alanlar: AnalizAlanlari }) =>
      analizApi.ekle(numuneId, alanlar),
    onSuccess: (s) => { tazele(); toast(s.mesaj) },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Analiz kaydedilemedi'),
  })

  function numuneSekmesiAc(n: NumuneOzet) {
    sekmeler.ac({ id: `n-${n.id}`, baslik: n.no, icerik: <NumuneSekmesi id={n.id} /> })
  }

  // Panelde form ALANLARI `.insp-b` içinde, kaydet butonu `.insp-f` altlığında
  // durur (ref:562-565): gövde kaydırılırken buton hep görünür kalsın diye.
  // İkisi ayrı DOM dallarında olduğu için form durumu burada, ortak atada tutulur.
  const form = useAnalizFormu({
    tip: detay?.numune.tip ?? 'lot',
    siraNo: (detay?.analizler.length ?? 0) + 1,
    bekliyor: analizEkle.isPending,
    yazabilir,
    // Panel başlığı fermenter/üründe kaynağın adını da yazar (ref:532 · 549);
    // lotta ek yok.
    kaynakAd:
      detay?.numune.tip === 'fermenter'
        ? (detay.numune.uniteAd ?? detay.numune.uniteKod ?? null)
        : detay?.numune.tip === 'urun'
          ? (detay.numune.stokAd ?? null)
          : null,
    esikler: ozet?.esikler,
    onKaydet: (alanlar) => {
      if (detay) analizEkle.mutate({ numuneId: detay.numune.id, alanlar })
    },
  })

  return (
    <>
      <div className="crumb">
        Operasyon <b>/ Laboratuvar</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Laboratuvar</h1>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            disabled
            title="Faz 7 kapsamı dışında — dışa aktarma henüz bağlanmadı"
            onClick={() => undefined}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Excel’e aktar
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Yeni numune',
                altBaslik: 'Üç numune tipi (K22) — alanlar tipe göre değişir',
                boyut: 'sm',
                icerik: <YeniNumuneDrawer />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Yeni numune
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Bekleyen Numune</div>
          <div className="k-v amber">{ozet?.bekleyen ?? '—'}</div>
          <div className="k-s">analiz girişi bekliyor</div>
        </div>
        <div className="k">
          <div className="k-l">Bugün Girilen Analiz</div>
          <div className="k-v">{ozet?.bugunAnaliz ?? '—'}</div>
          <div className="k-s">tüm numune tipleri</div>
        </div>
        <div className="k">
          <div className="k-l">Bu Ay Analiz</div>
          <div className="k-v">{ozet?.buAyAnaliz ?? '—'}</div>
          <div className="k-s">toplam kayıt</div>
        </div>
        <div className="k">
          <div className="k-l">Eşik Dışı Uyarı</div>
          <div className={ozet?.esikDisiUyari ? 'k-v red' : 'k-v'}>
            {ozet?.esikDisiUyari ?? '—'}
          </div>
          <div className="k-s">son 7 gün · proses/kalite</div>
        </div>
        <div className="k">
          <div className="k-l">FOS/TAC Son</div>
          <div
            className={
              ozet?.fosTacBant === 'kritik'
                ? 'k-v red'
                : ozet?.fosTacBant === 'izle'
                  ? 'k-v amber'
                  : 'k-v'
            }
          >
            {tr(ozet?.fosTacSon, 2)}
          </div>
          <div className="k-s">
            {ozet?.fosTacUnite ?? '—'}
            {ozet?.fosTacBant ? ` · ${ozet.fosTacBant === 'saglikli' ? 'sağlıklı' : ozet.fosTacBant}` : ''}
          </div>
        </div>
      </div>

      <div className="list-grid">
        <div className="card">
          <div className="toolbar">
            <div className="seg">
              {TIPLER.map((t) => (
                <button
                  key={t.anahtar}
                  type="button"
                  className={tip === t.anahtar ? 'seg-i on' : 'seg-i'}
                  onClick={() => { setTip(t.anahtar); setSayfa(1) }}
                >
                  {t.etiket}
                  <small>{sayac(t.anahtar)}</small>
                </button>
              ))}
            </div>
            <button
              className={sadeceBekleyen ? 'chip on' : 'chip'}
              type="button"
              onClick={() => { setSadeceBekleyen((b) => !b); setSayfa(1) }}
            >
              Sadece bekleyenler
            </button>
            <div className="f-search">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Numune, lot, substrat, ünite ara…"
                value={arama}
                onChange={(e) => { setArama(e.target.value); setSayfa(1) }}
              />
            </div>
            <div className="toolbar-right">
              <span>{liste.length} numune</span>
            </div>
          </div>

          <table className="grid">
            <thead>
              <tr>
                <th style={{ width: 96 }}>Numune</th>
                <th style={{ width: 82 }}>Tip</th>
                <th>Kaynak</th>
                <th style={{ width: 126 }}>Lot</th>
                <th style={{ width: 96 }}>Alınma</th>
                <th style={{ width: 76 }}>Alan</th>
                <th className="num" style={{ width: 58 }}>Analiz</th>
                <th style={{ width: 132 }}>Son Değer</th>
                <th style={{ width: 44 }} />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td></tr>
              )}
              {!isLoading && liste.length === 0 && (
                <tr><td colSpan={9} className="cell-sub" style={{ padding: 14 }}>Kayıt yok.</td></tr>
              )}
              {sayfaListesi.map((n) => {
                const k = kaynakMetni(n)
                return (
                  <tr
                    key={n.id}
                    className={seciliId === n.id ? 'sel' : ''}
                    onClick={() => setSeciliId(n.id)}
                    onDoubleClick={() => numuneSekmesiAc(n)}
                  >
                    <td>
                      {/* Rozet referansta sekmeyi açan tetikleyici (ref:479);
                          `.numb` zaten cursor:pointer + hover vaat ediyor.
                          role/tabIndex referansta yok — klavye erişimi için. */}
                      <span
                        className={n.durum === 'bekliyor' ? 'numb bek' : 'numb'}
                        title={
                          n.durum === 'bekliyor'
                            ? 'Analiz bekliyor · tam geçmişi aç'
                            : 'tam geçmişi aç'
                        }
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); numuneSekmesiAc(n) }}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter' && e.key !== ' ') return
                          e.preventDefault()
                          e.stopPropagation()
                          numuneSekmesiAc(n)
                        }}
                      >
                        {n.no}
                      </span>
                    </td>
                    <td className="tur-t">{TIP_ETIKET[n.tip]}</td>
                    <td>
                      <div className="cell-name" style={{ fontWeight: 500 }}>{k.ust}</div>
                      <div className="cell-sub">{k.alt}</div>
                    </td>
                    <td>
                      {n.lotBelgeNo
                        ? <span className="kod">{n.lotBelgeNo}</span>
                        : <span style={{ color: '#8A98AB' }}>—</span>}
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>{kisaZaman(n.alinmaZamani)}</td>
                    <td>{n.alanKisi ?? '—'}</td>
                    <td className="num">{n.analizSayisi}</td>
                    <td style={{ fontSize: 11.5 }}><SonDegerHucresi deger={n.sonDeger} /></td>
                    <td>
                      <div className="row-acts">
                        {/* Referans satır aksiyonunda KALEM ikonu ve "Analiz gir"
                            başlığı kullanıyor (ref:486); ↗ yalnız panel
                            altlığına ait (ref:563) — iki niyet ayrı görünmeli. */}
                        <button
                          className="mini-btn"
                          type="button"
                          title="Analiz gir"
                          onClick={(e) => { e.stopPropagation(); numuneSekmesiAc(n) }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <div className="grid-foot">
            <span>Toplam {hepsi.length} numune · 3 tip (K22)</span>
            <Sayfalayici gecerli={gecerliSayfa} toplam={sayfaSayisi} git={setSayfa} />
          </div>

          {/* Altlık sayfalayıcıya döndüğü için K15 notu kartın dipnotuna indi. */}
          <div className="pnote">
            Analizler silinmez — düzeltme revizyondur, sayaç artar (K15)
          </div>
        </div>

        <aside className="insp card" ref={panel}>
          {!detay ? (
            <div className="insp-empty">Bir numune seçin — analiz girişi burada yapılır.</div>
          ) : (
            <>
              <div className="insp-h">
                <div className="insp-eyebrow">
                  <span className="kod">{detay.numune.no}</span>
                  <span>·</span>
                  <span>{TIP_ETIKET[detay.numune.tip]}</span>
                  <span>·</span>
                  <span>{kisaZaman(detay.numune.alinmaZamani)}</span>
                  <span>·</span>
                  <span>{detay.numune.alanKisi ?? '—'}</span>
                </div>
                <div className="insp-t">
                  {kaynakMetni(detay.numune).ust}{' '}
                  <span className={DURUM_ROZET[detay.numune.durum].sinif}>
                    {DURUM_ROZET[detay.numune.durum].metin}
                  </span>
                </div>
                {/* Yalnız lotta belge zinciri (ref:512). Kabul fişi no §5.1'de
                    yok; lot belge no + cari basılıyor. */}
                {detay.numune.tip === 'lot' && (
                  <div className="cell-sub" style={{ marginTop: 2 }}>
                    {[detay.numune.lotBelgeNo, detay.numune.cariUnvan]
                      .filter(Boolean).join(' · ') || '—'}
                  </div>
                )}
              </div>

              {/* Referans sırası (ref:565): form → son analizler → koyu kart → trend */}
              <div className="insp-b">
                {/* Panelde başlık `.insp-sec-t` (ref:518); sekmede `.sect-t`. */}
                <div className="insp-sec">
                  <div className="insp-sec-t">{form.baslik}</div>
                  {form.govde}
                </div>

                <SonAnalizler detay={detay} />

                {/* LOT: koyu KM kıyas kartı (K13 · K32) */}
                {detay.kmKiyas && (
                  <div className="insp-sec">
                    <div className="inst">
                      <div className="inst-t">KM Kıyas — Derin Analiz</div>
                      <div className="inst-row">
                        <span className="inst-l">Numune ort.</span>
                        <span className="inst-v">{tr(detay.kmKiyas.numuneOrt, 2)}</span>
                      </div>
                      <div className="inst-row">
                        <span className="inst-l">Stok kartı</span>
                        <span className="inst-v">{tr(detay.kmKiyas.stokKarti, 2)}</span>
                      </div>
                      <div className="inst-row">
                        <span className="inst-l">Substrat geneli</span>
                        <span className="inst-v">{tr(detay.kmKiyas.substratGeneli, 2)}</span>
                      </div>
                      <div className="inst-row">
                        <span className="inst-l">{detay.kmKiyas.cariUnvan ?? 'Cari'} ort.</span>
                        <span className="inst-v">{tr(detay.kmKiyas.cariOrt, 2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* FERMENTER: koyu proses sağlığı kartı (ref:541-545) */}
                {detay.numune.tip === 'fermenter' && (
                  <div className="insp-sec">
                    <ProsesSagligiKarti detay={detay} bosNot="İlk analiz bekleniyor" />
                  </div>
                )}

                {/* ÜRÜN: koyu gübre değeri kartı (ref:555-558) */}
                {detay.numune.tip === 'urun' && (
                  <div className="insp-sec">
                    <GubreDegeriKarti detay={detay} bosNot="İlk analiz bekleniyor" />
                  </div>
                )}

                {/* FERMENTER: FOS/TAC trendi */}
                {detay.numune.tip === 'fermenter' && (
                  <div className="insp-sec">
                    <div className="insp-sec-t">
                      FOS/TAC Trendi — {detay.numune.uniteAd ?? detay.numune.uniteKod}
                    </div>
                    <div className="mini-list">
                      {detay.fosTacTrendi.length === 0 ? (
                        <div className="mini-li" style={{ color: 'var(--faint)' }}>Veri yok</div>
                      ) : (
                        detay.fosTacTrendi.map((t, i) => (
                          <div className="mini-li" key={i}>
                            <span className="dt">{kisaZaman(t.zaman)}</span>
                            pH {tr(t.ph, 2)}
                            <span className={t.bant === 'saglikli' ? 'vv' : 'vv minus'}>
                              {tr(t.fosTac, 2)}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="insp-f">
                <button
                  className="btn btn-sec btn-sm"
                  type="button"
                  style={{ padding: '0 8px' }}
                  title="Tam geçmiş ve derin kıyas (sekme)"
                  onClick={() => numuneSekmesiAc(detay.numune)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                  </svg>
                </button>
                <button
                  className="btn btn-pri btn-sm"
                  type="button"
                  style={{ flex: 1, justifyContent: 'center' }}
                  disabled={!yazabilir || analizEkle.isPending}
                  title={yazabilir ? undefined : 'Laboratuvar modülünde yazma yetkiniz yok'}
                  onClick={form.kaydet}
                >
                  {form.butonMetni}
                </button>
              </div>
            </>
          )}
        </aside>
      </div>
    </>
  )
}

/**
 * Tablonun "Son Değer" hücresi (ref:454-457): etiket düz metin, sayı
 * `<b class="mono">`, fermenterde eşik dışı ölçüm renkli. Ölçüm yoksa
 * referanstaki soluk tire.
 */
function SonDegerHucresi({ deger }: { deger: NumuneOzet['sonDeger'] }) {
  if (!deger) return <span style={{ color: '#8A98AB' }}>—</span>

  return (
    <>
      {deger.etiket}{' '}
      <b
        className="mono"
        style={{
          color:
            deger.bant === 'kritik'
              ? 'var(--red)'
              : deger.bant === 'izle'
                ? 'var(--amber)'
                : 'inherit',
        }}
      >
        {tr(deger.deger, deger.basamak)}
      </b>
      {deger.birim ? ` ${deger.birim}` : ''}
    </>
  )
}

const ALAN_ETIKET: Record<string, string> = {
  ph: 'pH',
  fosTac: 'FOS/TAC',
  nh4: 'NH₄-N',
  vfa: 'VFA',
}

/** §6.3 bandı → koyu karttaki değer sınıfı (rozet değil, `.inst-v` varyantı). */
const INST_BANT: Record<string, string> = {
  saglikli: 'inst-v',
  izle: 'inst-v amber',
  kritik: 'inst-v red',
}

/** Koyu kartların ölçüm kaynağı: numunenin EN SON analizi (sunucu sıraya koyuyor). */
export function sonAnaliz(detay: NumuneDetay): AnalizDto | null {
  return detay.analizler.at(-1) ?? null
}

/**
 * Fermenter numunesinin koyu "Proses Sağlığı" kartı (ref:541-545 · 710-713).
 * Bantlar `ayar_esik`'ten geldiği için eşik değişince kart kendiliğinden
 * yeni rengi gösterir; alt not referanstaki sabit özet satırıdır.
 */
export function ProsesSagligiKarti({
  detay,
  bosNot,
  stil,
}: {
  detay: NumuneDetay
  /** Panelde "İlk analiz bekleniyor", sekmede "Veri bekleniyor" (ref:544 · 712). */
  bosNot: string
  stil?: CSSProperties
}) {
  return (
    <div className="inst" style={stil}>
      <div className="inst-t">
        Proses Sağlığı — {detay.numune.uniteAd ?? detay.numune.uniteKod ?? '—'}
      </div>
      {detay.bantlar.length === 0 ? (
        <div className="inst-note">{bosNot}</div>
      ) : (
        detay.bantlar.map((b) => (
          <div className="inst-row" key={b.alan}>
            <span className="inst-l">{ALAN_ETIKET[b.alan] ?? b.alan}</span>
            <span className={INST_BANT[b.bant] ?? 'inst-v'}>
              {tr(b.deger, 2)} <small>{b.etiket}</small>
            </span>
          </div>
        ))
      )}
      <div className="inst-note">
        {'FOS/TAC ≤0,4 · NH₄ <3000 · pH 7,0–7,8 · VFA <1500'}
      </div>
    </div>
  )
}

/** Ürün numunesinin koyu "Gübre Değeri" kartı (ref:555-558 · 727-731). */
export function GubreDegeriKarti({
  detay,
  bosNot,
  stil,
}: {
  detay: NumuneDetay
  bosNot: string
  stil?: CSSProperties
}) {
  const son = sonAnaliz(detay)

  return (
    <div className="inst" style={stil}>
      <div className="inst-t">Gübre Değeri — kg/t</div>
      {son ? (
        <>
          <div className="inst-3">
            <div className="inst-c">
              <div className="inst-v">{tr(son.n, 1)}</div>
              <div className="inst-l">N</div>
            </div>
            <div className="inst-c">
              <div className="inst-v">{tr(son.p, 1)}</div>
              <div className="inst-l">P₂O₅</div>
            </div>
            <div className="inst-c">
              <div className="inst-v">{tr(son.k, 1)}</div>
              <div className="inst-l">K₂O</div>
            </div>
          </div>
          {son.nem != null && (
            <div className="inst-note">Nem: %{tr(son.nem, 1)} — satış şartnamesi</div>
          )}
        </>
      ) : (
        <div className="inst-note">{bosNot}</div>
      )}
    </div>
  )
}

/**
 * Referans sonListe() (ref:498-505): en yeni 3 kayıt, tersten. Bölüm kayıt
 * yokken de basılır — bekleyen numunede panel eksik açılmasın diye.
 */
export function SonAnalizler({ detay }: { detay: import('./api').NumuneDetay }) {
  const a = detay.analizler

  const ozet = (x: import('./api').AnalizDto) => {
    if (detay.numune.tip === 'lot')
      return `KM ${tr(x.km, 2)} · OKM ${tr(x.okm, 2)} · pH ${tr(x.ph, 2)}`
    if (detay.numune.tip === 'fermenter')
      return `pH ${tr(x.ph, 2)} · F/T ${tr(x.fosTac, 2)} · NH₄ ${tr(x.nh4, 0)}`
    return `N ${tr(x.n, 1)} · P ${tr(x.p, 1)} · K ${tr(x.k, 1)}`
  }

  return (
    <div className="insp-sec">
      <div className="insp-sec-t">Son Analizler ({a.length})</div>
      <div className="mini-list" style={{ margin: '-2px 0' }}>
        {a.length === 0 ? (
          <div className="mini-li" style={{ color: 'var(--faint)' }}>Henüz analiz yok</div>
        ) : (
          [...a].slice(-3).reverse().map((x) => (
            <div className="mini-li" key={x.id}>
              <span className="dt">{kisaTarih(x.zaman)}</span>
              <span style={{ fontSize: 11 }}>{ozet(x)}</span>
              <span className="vv">
                #{x.siraNo}
                {x.revizyonNo > 0 ? ` · rev ${x.revizyonNo}` : ''}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

export default function LaboratuvarSayfasi() {
  return (
    <Kabuk listeBasligi="Laboratuvar">
      <YetkiKapisi modul="Laboratuvar">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
